import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ENO1StratumData } from './useENO1StratumData';

// Extended interface with fileId for mutation operations
interface ENO1StratumMutationData extends ENO1StratumData {
  fileId?: number;
}

/**
 * Hook for updating an existing eNO1 stratum
 * @returns Mutation for updating eNO1 stratum data
 */
export function useUpdateENO1Stratum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stratumData: ENO1StratumMutationData) => {
      // Update to use '/ipc/stratum/eno1' without the ID in the URL path
      const response = await axios.put('/ipc/stratum/eno1', stratumData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Get the fileId from the response or fallback to a property in variables
      const fileId = data?.fileId || variables?.fileId;
      
      // Invalidate queries to trigger a refetch
      if (fileId) {
        queryClient.invalidateQueries({ 
          queryKey: ['eno1StratumData', fileId]
        });
        
        // Also invalidate sample data as stratum changes might affect them
        queryClient.invalidateQueries({ 
          queryKey: ['eno1Samples', fileId]
        });
      }
    }
  });
}

/**
 * Hook for adding a new eNO1 stratum
 * @returns Mutation for adding a new eNO1 stratum
 */
export function useAddENO1Stratum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stratumData: Omit<ENO1StratumData, 'stratumId'> & { fileId: number }) => {
      const response = await axios.post('/ipc/stratum/eno1', stratumData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Get the fileId from the response or fallback to a property in variables
      const fileId = data?.fileId || variables?.fileId;
      
      // Invalidate queries to trigger a refetch
      if (fileId) {
        queryClient.invalidateQueries({ 
          queryKey: ['eno1StratumData', fileId]
        });
        
        // Also invalidate sample data as new stratum might affect them
        queryClient.invalidateQueries({ 
          queryKey: ['eno1Samples', fileId]
        });
      }
    }
  });
} 